import { html } from 'lit-html';
import { authService, AuthService } from '../services/auth.js';
import { userService } from '../services/user.js';
import { clientService } from '../services/client.js';
import { themeService } from '../services/theme.js';
import { router } from '../router.js';
import { showSuccess, showError } from './notifications.js';
import { Button } from './ui/button.js';
import { userContextService } from '../services/user-context.js';
import { demoModeService } from '../services/demo-mode.js';
import { subscriptionGuard } from '../services/subscription-guard.js';
import { t } from '../i18n/index.js';

// Helper para acessar estado do header do cliente de forma segura
const getClientHeaderState = () => AuthService.getClientHeaderState();
const setClientHeaderState = (state) => AuthService.setClientHeaderState(state);


// Header profissional e minimalista para clientes
const renderClientHeader = (clientContext) => {
  const currentTheme = themeService.getTheme();

  const handleThemeToggle = () => {
    themeService.toggleTheme();
  };

  // Estado para armazenar informações da agência (usando Map privado)
  let headerState = getClientHeaderState();
  if (!headerState) {
    headerState = {
      agencyName: null,
      clientName: null,
      agencyLogo: null,
      agencyLogoDark: null,
      loading: true
    };
    setClientHeaderState(headerState);

    // Buscar informações da agência e cliente (apenas nome para dropdown)
    loadClientHeaderInfo(clientContext.clientId);
  }

  // Usar logo da agência se disponível, senão fallback para SUMMDASH
  const getClientAgencyLogo = () => {
    if (headerState.agencyLogo || headerState.agencyLogoDark) {
      return currentTheme === 'dark' && headerState.agencyLogoDark
        ? headerState.agencyLogoDark
        : headerState.agencyLogo || headerState.agencyLogoDark;
    }
    return currentTheme === 'dark'
      ? '/images/logo_white.png'
      : '/images/logo_black.png';
  };
  const clientHeaderLogo = getClientAgencyLogo();

  return html`
    <header class="client-header-minimal">
      <div class="client-header-container">
        <!-- Logo da agência -->
        <div class="agency-section">
          <div class="agency-logo">
            <img src="${clientHeaderLogo}" alt="${headerState.agencyName || 'SummDash'}" class="logo-main">
          </div>
        </div>

        
        <!-- Seção de Ações -->
        <div class="header-actions-minimal">
          <!-- Botão de tema minimalista -->
          <button class="minimal-theme-btn" @click=${handleThemeToggle} title="${currentTheme === 'light' ? t('header.toggleDarkTheme') : t('header.toggleLightTheme')}">
            ${currentTheme === 'light' ? html`
              <i class="fas fa-moon" style="font-size: 16px;"></i>
            ` : html`
              <i class="fas fa-sun" style="font-size: 16px;"></i>
            `}
          </button>
          
          <!-- Menu do usuário minimalista -->
          <div class="client-menu-minimal">
            <button class="client-menu-btn" @click=${() => toggleClientDropdown()}>
              <div class="client-avatar-minimal">
                <i class="fas fa-user" style="font-size: 18px;"></i>
              </div>
              <i class="fas fa-chevron-down menu-arrow" style="font-size: 12px;"></i>
            </button>
            <!-- Dropdown criado via DOM para evitar re-renderização -->
          </div>
        </div>
      </div>
    </header>
  `;
};

// Função para carregar informações da agência e cliente (apenas nome para dropdown)
const loadClientHeaderInfo = async (clientId) => {
  try {
    const { clientValidationService } = await import('../services/client-validation.js');
    const clientInfo = await clientValidationService.getClientLoginInfo(clientId);

    if (clientInfo.found) {
      setClientHeaderState({
        agencyName: clientInfo.agencyName || 'SummDash',
        clientName: clientInfo.clientName || '',
        agencyLogo: clientInfo.agencyLogo || null,
        agencyLogoDark: clientInfo.agencyLogoDark || null,
        loading: false
      });
      // Atualizar logo no DOM se já carregado
      const logoImg = document.querySelector('.client-header-minimal .agency-logo img');
      if (logoImg && (clientInfo.agencyLogo || clientInfo.agencyLogoDark)) {
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        const newLogo = isDark && clientInfo.agencyLogoDark
          ? clientInfo.agencyLogoDark
          : clientInfo.agencyLogo || clientInfo.agencyLogoDark;
        logoImg.src = newLogo;
        logoImg.alt = clientInfo.agencyName || 'SummDash';
      }
    }
  } catch {
    setClientHeaderState({
      agencyName: 'SummDash',
      clientName: '',
      agencyLogo: null,
      agencyLogoDark: null,
      loading: false
    });
  }
};

// Função auxiliar para dropdown do cliente - manipula DOM diretamente para evitar re-renderização
const toggleClientDropdown = () => {
  window.clientDropdownOpen = !window.clientDropdownOpen;

  // Remove listener anterior se existir
  if (window._clientDropdownOutsideClickHandler) {
    document.removeEventListener('click', window._clientDropdownOutsideClickHandler);
    window._clientDropdownOutsideClickHandler = null;
  }

  const menuArrow = document.querySelector('.client-menu-btn .menu-arrow');
  const clientMenu = document.querySelector('.client-menu-minimal');

  // Atualiza estado visual da seta
  if (menuArrow) {
    if (window.clientDropdownOpen) {
      menuArrow.classList.add('open');
    } else {
      menuArrow.classList.remove('open');
    }
  }

  // Remove dropdown existente
  const existingDropdown = document.querySelector('.client-dropdown-minimal');
  if (existingDropdown) {
    existingDropdown.remove();
  }

  // Se abrindo o dropdown, criar via DOM
  if (window.clientDropdownOpen && clientMenu) {
    const clientContext = userContextService.getClientContext();
    const headerState = getClientHeaderState();
    const clientName = headerState?.clientName || t('header.client');
    const clientEmail = clientContext?.email || '';

    const dropdownHTML = `
      <div class="client-dropdown-minimal">
        <div class="dropdown-header-minimal">
          <div class="user-info-minimal">
            <span class="user-name-minimal">${clientName}</span>
            <span class="user-email-minimal">${clientEmail}</span>
          </div>
        </div>
        <div class="dropdown-divider-minimal"></div>
        <button class="dropdown-item-minimal client-logout-btn">
          <i class="fas fa-sign-out-alt" style="font-size: 14px;"></i>
          ${t('header.signOut')}
        </button>
      </div>
    `;

    clientMenu.insertAdjacentHTML('beforeend', dropdownHTML);

    // Adiciona event listener para logout
    const logoutBtn = clientMenu.querySelector('.client-logout-btn');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        // Limpar estado do header antes de fazer logout (usando Map privado)
        AuthService.clearClientHeaderState();
        window.clientDropdownOpen = false;
        userContextService.logoutClient();
        showSuccess(t('header.logoutSuccess'));
        router.navigate(`/clients/login/${clientContext.clientId}`);
      });
    }

    // Adicionar listener para fechar ao clicar fora
    setTimeout(() => {
      window._clientDropdownOutsideClickHandler = (e) => {
        const dropdown = document.querySelector('.client-dropdown-minimal');
        const menuBtn = document.querySelector('.client-menu-btn');

        // Se clicou fora do dropdown e fora do botão, fechar
        if (dropdown && !dropdown.contains(e.target) &&
            menuBtn && !menuBtn.contains(e.target)) {
          window.clientDropdownOpen = false;
          if (menuArrow) menuArrow.classList.remove('open');
          dropdown.remove();
          document.removeEventListener('click', window._clientDropdownOutsideClickHandler);
          window._clientDropdownOutsideClickHandler = null;
        }
      };
      document.addEventListener('click', window._clientDropdownOutsideClickHandler);
    }, 10);
  }
};

export const dashboardHeader = () => {
  // Detectar tipo de usuário (sempre re-detectar para garantir consistência)
  userContextService.detectUserType();
  const userType = userContextService.getUserType();
  const isClient = userContextService.isClient();
  const clientContext = userContextService.getClientContext();
  
  // Se for cliente, mostrar header simplificado
  if (isClient && clientContext) {
    return renderClientHeader(clientContext);
  }
  
  // Se mudou de cliente para agência, limpar estado do header de cliente
  if (userType === 'agency' && getClientHeaderState()) {
    AuthService.clearClientHeaderState();
  }
  
  // Header completo para agência
  const user = authService.getUserProfile();
  const agency = userService.getCurrentAgency();
  const clientCount = clientService.getClientCount();
  const maxClients = userService.getMaxClients();
  const isNearLimit = clientCount >= maxClients - 2;
  const currentTheme = themeService.getTheme();

  // Usar logo da agência se disponível, senão fallback para SUMMDASH
  const getAgencyLogo = () => {
    if (agency?.logo || agency?.logoDark) {
      return currentTheme === 'dark' && agency.logoDark
        ? agency.logoDark
        : agency.logo || agency.logoDark;
    }
    return currentTheme === 'dark'
      ? '/images/logo_white.png'
      : '/images/logo_black.png';
  };
  const headerLogo = getAgencyLogo();

  const handleThemeToggle = () => {
    themeService.toggleTheme();
  };
  
  const handleLogout = async () => {
    try {
      // Fechar dropdown primeiro
      setDropdownOpen(false);

      // Fazer logout do Firebase
      await authService.signOut();

      // Limpar estados locais
      if (window.headerState) {
        delete window.headerState;
      }

      showSuccess(
        t('header.logoutSuccess'),
        t('header.logoutSuccessMessage')
      );

      // Navegar para landing page
      router.navigate('/');
    } catch {
      showError(
        t('header.logoutError'),
        t('header.logoutErrorMessage')
      );
    }
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState('mobileMenuOpen', false);
  const [dropdownOpen, setDropdownOpen] = useState('dropdownOpen', false);
  
  const toggleMobileMenu = () => {
    const currentValue = window.headerState?.mobileMenuOpen ?? false;
    const newValue = !currentValue;
    setMobileMenuOpen(newValue);
    // Body scroll lock
    document.body.style.overflow = newValue ? 'hidden' : '';
    // Escape key listener
    if (newValue) {
      const escHandler = (e) => {
        if (e.key === 'Escape') {
          setMobileMenuOpen(false);
          document.body.style.overflow = '';
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);
    }
  };

  const toggleDropdown = () => {
    // Ler o valor atual do estado, não o valor capturado na renderização
    const currentValue = window.headerState?.dropdownOpen ?? false;
    setDropdownOpen(!currentValue);
  };
  
  // Função auxiliar para useState
  function useState(key, initialValue) {
    if (!window.headerState) window.headerState = {};
    if (window.headerState[key] === undefined) {
      window.headerState[key] = initialValue;
    }
    return [
      window.headerState[key],
      (newValue) => {
        window.headerState[key] = newValue;

        // Estados visuais do header não precisam re-renderizar o app todo
        // Apenas manipulam o DOM diretamente para melhor performance
        if (key === 'mobileMenuOpen') {
          // Para o menu mobile, apenas atualiza as classes CSS diretamente
          const menu = document.querySelector('.dashboard-menu');
          let overlay = document.querySelector('.mobile-menu-overlay');
          const menuIcon = document.querySelector('.menu-icon');
          const header = document.querySelector('.dashboard-header');
          // Restore body scroll on close
          document.body.style.overflow = newValue ? 'hidden' : '';

          if (menu) {
            if (newValue) {
              menu.classList.add('mobile-open');
            } else {
              menu.classList.remove('mobile-open');
            }
          }

          // Criar ou remover o overlay dinamicamente
          if (newValue && !overlay && header) {
            // Criar overlay
            overlay = document.createElement('div');
            overlay.className = 'mobile-menu-overlay active';
            overlay.addEventListener('click', () => {
              setMobileMenuOpen(false);
            });
            header.insertBefore(overlay, header.firstChild);
          } else if (!newValue && overlay) {
            // Remover overlay
            overlay.remove();
          }

          if (menuIcon) {
            if (newValue) {
              menuIcon.classList.add('open');
            } else {
              menuIcon.classList.remove('open');
            }
          }
        } else if (key === 'dropdownOpen') {
          // Para o dropdown do usuário, manipula DOM diretamente
          const dropdownArrow = document.querySelector('.dropdown-arrow');
          const userDropdown = document.querySelector('.user-dropdown');
          const userMenu = document.querySelector('.user-menu');

          if (dropdownArrow) {
            if (newValue) {
              dropdownArrow.classList.add('open');
            } else {
              dropdownArrow.classList.remove('open');
            }
          }

          if (userMenu) {
            // Remove dropdown existente
            const existingDropdown = userMenu.querySelector('.user-dropdown');
            if (existingDropdown) {
              existingDropdown.remove();
            }

            // Remove listener anterior se existir
            if (window._dropdownOutsideClickHandler) {
              document.removeEventListener('click', window._dropdownOutsideClickHandler);
              window._dropdownOutsideClickHandler = null;
            }

            // Cria novo dropdown se necessário
            if (newValue) {
              const user = authService.getCurrentUser();
              const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
              const themeIcon = currentTheme === 'light'
                ? `<i class="fas fa-moon" style="font-size: 18px;"></i>`
                : `<i class="fas fa-sun" style="font-size: 18px;"></i>`;
              const themeText = currentTheme === 'light' ? t('header.darkTheme') : t('header.lightTheme');

              const dropdownHTML = `
                <div class="user-dropdown">
                  <div class="dropdown-header">
                    <span class="user-name">${user?.displayName || t('header.user')}</span>
                    <span class="user-email">${user?.email || ''}</span>
                  </div>
                  <div class="dropdown-divider"></div>
                  <button class="dropdown-item dropdown-theme-toggle">
                    ${themeIcon}
                    ${themeText}
                  </button>
                  <button class="dropdown-item dropdown-subscription">
                    <i class="fas fa-calendar-alt" style="font-size: 18px;"></i>
                    ${t('header.mySubscription')}
                  </button>
                  <button class="dropdown-item dropdown-agency-settings">
                    <i class="fas fa-building" style="font-size: 18px;"></i>
                    ${t('header.agencySettings')}
                  </button>
                  <button class="dropdown-item dropdown-support">
                    <i class="fas fa-headset" style="font-size: 18px;"></i>
                    ${t('header.support')}
                  </button>
                  <button class="dropdown-item dropdown-logout">
                    <i class="fas fa-sign-out-alt" style="font-size: 18px;"></i>
                    ${t('header.signOut')}
                  </button>
                </div>
              `;

              userMenu.insertAdjacentHTML('beforeend', dropdownHTML);

              // Adiciona event listeners
              const themeToggleBtn = userMenu.querySelector('.dropdown-theme-toggle');
              const subscriptionBtn = userMenu.querySelector('.dropdown-subscription');
              const agencySettingsBtn = userMenu.querySelector('.dropdown-agency-settings');
              const supportBtn = userMenu.querySelector('.dropdown-support');
              const logoutBtn = userMenu.querySelector('.dropdown-logout');

              if (themeToggleBtn) {
                themeToggleBtn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  import('../services/theme.js').then(({ themeService }) => {
                    themeService.toggleTheme();
                  });
                });
              }

              if (subscriptionBtn) {
                subscriptionBtn.addEventListener('click', () => {
                  setDropdownOpen(false);
                  window.router.navigate('/subscription');
                });
              }

              if (agencySettingsBtn) {
                agencySettingsBtn.addEventListener('click', () => {
                  setDropdownOpen(false);
                  window.router.navigate('/agency-settings');
                });
              }

              if (supportBtn) {
                supportBtn.addEventListener('click', async () => {
                  setDropdownOpen(false);
                  const { supportModal } = await import('./support-modal.js');
                  supportModal.open();
                });
              }

              if (logoutBtn) {
                logoutBtn.addEventListener('click', async () => {
                  setDropdownOpen(false);
                  try {
                    await authService.signOut();
                    window.router.navigate('/');
                  } catch {
                    // silent
                  }
                });
              }

              // Adicionar listener para fechar ao clicar fora do dropdown
              setTimeout(() => {
                window._dropdownOutsideClickHandler = (e) => {
                  const dropdown = document.querySelector('.user-dropdown');
                  const avatarWrapper = document.querySelector('.user-avatar-wrapper');

                  // Se clicou fora do dropdown e fora do avatar, fechar
                  if (dropdown && !dropdown.contains(e.target) &&
                      avatarWrapper && !avatarWrapper.contains(e.target)) {
                    setDropdownOpen(false);
                  }
                };
                document.addEventListener('click', window._dropdownOutsideClickHandler);
              }, 10); // Pequeno delay para não capturar o click que abriu o dropdown
            }
          }
        } else {
          // Para outros estados, re-renderiza o app
          import('../main.js').then(({ renderApp }) => renderApp());
        }
      }
    ];
  }
  
  return html`
    <header class="dashboard-header">
      <!-- Mobile Menu Overlay será criado dinamicamente via JavaScript -->

      <div class="dashboard-nav">
        <div class="mobile-header">
          ${Button({
            onClick: toggleMobileMenu,
            variant: 'ghost',
            size: 'small',
            className: 'mobile-menu-button',
            icon: html`
              <svg class="menu-icon ${mobileMenuOpen ? 'open' : ''}" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line class="line line-top" x1="3" y1="6" x2="21" y2="6"></line>
                <line class="line line-middle" x1="3" y1="12" x2="21" y2="12"></line>
                <line class="line line-bottom" x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            `
          })}
          <div class="logo">
            <img src="${headerLogo}" alt="${agency?.name || 'SummDash'}" class="logo-main">
          </div>
        </div>

        ${(() => { subscriptionGuard.clearCache(); return subscriptionGuard.hasFullAccess() || demoModeService.getStatus(); })() ? html`
        <nav class="dashboard-menu ${mobileMenuOpen ? 'mobile-open' : ''}">
          <a href="/dashboard" class="nav-item ${router.currentPath === '/dashboard' ? 'active' : ''}" @click=${(e) => { e.preventDefault(); setMobileMenuOpen(false); router.navigate('/dashboard'); }}>
            <i class="fas fa-th-large" style="font-size: 18px;"></i>
            ${t('nav.dashboard')}
          </a>
          <a href="/clients" class="nav-item ${router.currentPath === '/clients' || router.currentPath.startsWith('/clients/') ? 'active' : ''}" @click=${(e) => { e.preventDefault(); setMobileMenuOpen(false); router.navigate('/clients'); }}>
            <i class="fas fa-users" style="font-size: 18px;"></i>
            ${t('nav.clients')}
          </a>
          <a href="/settings" class="nav-item ${router.currentPath === '/settings' ? 'active' : ''}" @click=${(e) => { e.preventDefault(); setMobileMenuOpen(false); router.navigate('/settings'); }}>
            <i class="fas fa-cog" style="font-size: 18px;"></i>
            ${t('nav.settings')}
          </a>
        </nav>
        ` : ''}
        
        <div class="header-right">
          <!-- Toggle Modo Demo (apenas quando app está pronto e sem assinatura ativa) -->
          ${(() => {
            const hasAccess = subscriptionGuard.hasFullAccess();
            // Auto-desativar demo mode se usuário tem assinatura ativa
            if (hasAccess && demoModeService.getStatus()) {
              demoModeService.disable();
            }
            return !hasAccess;
          })() ? html`
            <div class="demo-mode-toggle ${demoModeService.getStatus() ? 'active' : ''}"
                 @click=${() => {
                   // Dismiss tooltip on any toggle click
                   localStorage.setItem('summdash_demo_tooltip_dismissed', 'true');
                   const tooltip = document.querySelector('.demo-tooltip');
                   if (tooltip) tooltip.remove();
                   const newStatus = demoModeService.toggle();
                   // Navegar diretamente sem mostrar notificação (evita render extra)
                   // O toggle visual e a navegação já fornecem feedback suficiente
                   if (newStatus) {
                     // Ativando demo → vai para dashboard
                     if (router.currentPath === '/dashboard') {
                       // Já está no dashboard, apenas re-renderiza
                       import('../main.js').then(({ renderApp }) => renderApp());
                     } else {
                       router.navigate('/dashboard');
                     }
                   } else {
                     // Desativando demo → vai para checkout
                     if (router.currentPath === '/checkout') {
                       // Já está no checkout, apenas re-renderiza
                       import('../main.js').then(({ renderApp }) => renderApp());
                     } else {
                       router.navigate('/checkout');
                     }
                   }
                 }}
                 title="${demoModeService.getStatus() ? t('header.demoMode.deactivate') : t('header.demoMode.activate')}">
              <div class="demo-toggle-track">
                <div class="demo-toggle-thumb"></div>
              </div>
              <span class="demo-toggle-label">
                ${demoModeService.getStatus() ? html`
                  <i class="fas fa-eye" style="font-size: 12px;"></i>
                  ${t('header.demoMode.label')}
                ` : html`
                  <i class="fas fa-eye-slash" style="font-size: 12px;"></i>
                  ${t('header.demoMode.label')}
                `}
              </span>
              ${!demoModeService.getStatus() && !localStorage.getItem('summdash_demo_tooltip_dismissed') ? html`
                <div class="demo-tooltip">
                  <p class="demo-tooltip-text">${t('header.demoMode.tooltip')}</p>
                  <button class="demo-tooltip-dismiss" @click=${(e) => {
                    e.stopPropagation();
                    localStorage.setItem('summdash_demo_tooltip_dismissed', 'true');
                    // Ativa o demo mode
                    demoModeService.toggle();
                    if (router.currentPath === '/dashboard') {
                      import('../main.js').then(({ renderApp }) => renderApp());
                    } else {
                      router.navigate('/dashboard');
                    }
                  }}>${t('header.demoMode.activate')}</button>
                </div>
              ` : ''}
            </div>
          ` : ''}

          <!-- Indicador do Plano (oculto sem assinatura e sem demo) -->
          ${subscriptionGuard.hasFullAccess() || demoModeService.getStatus() ? html`
          <div class="plan-indicator">
            <div class="client-counter-wrapper ${isNearLimit ? 'warning' : ''}">
              <div class="client-counter-slider">
                <div class="counter-slide counter-info">
                  <i class="fas fa-users" style="font-size: 12px;"></i>
                  <span>${demoModeService.getStatus() ? '5/5' : `${clientCount}/${maxClients}`}</span>
                </div>
              </div>
            </div>
          </div>
          ` : ''}

          <!-- Seção de ações do usuário -->
          <div class="user-actions">
            <div class="user-menu">
              <div class="user-avatar-wrapper" @click=${toggleDropdown}>
              ${user?.photoURL ? html`
                <img src="${user.photoURL}" alt="${user?.displayName}" class="user-avatar">
              ` : html`
                <div class="user-avatar user-avatar-initial">
                  ${user?.email?.charAt(0) || 'U'}
                </div>
              `}
              <i class="fas fa-chevron-down dropdown-arrow ${dropdownOpen ? 'open' : ''}" style="font-size: 10px;"></i>
            </div>
            <!-- Dropdown rendered via DOM manipulation in useState setter -->
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Trial Banner -->
    ${(() => {
      const subStatus = subscriptionGuard.getSubscriptionStatus();
      if (subStatus.status !== 'trial' || !subStatus.trialEndsAt) return '';
      const now = new Date();
      const end = new Date(subStatus.trialEndsAt);
      const daysLeft = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
      return html`
        <div class="trial-banner">
          <div class="trial-banner-content">
            <i class="fas fa-clock" style="font-size: 14px;"></i>
            <span>${t('trial.banner.message', { days: daysLeft })}</span>
            <a href="/checkout" class="trial-banner-cta" @click=${(e) => { e.preventDefault(); router.navigate('/checkout'); }}>
              ${t('trial.banner.subscribe')}
            </a>
          </div>
        </div>
      `;
    })()}

    <!-- Bottom Tab Navigation - Mobile Only (esconde quando sem assinatura ativa e modo demo desativado) -->
    ${(() => { return subscriptionGuard.hasFullAccess() || demoModeService.getStatus(); })() ? html`
    <nav class="mobile-bottom-nav">
      <a href="/dashboard" class="bottom-nav-item ${router.currentPath === '/dashboard' ? 'active' : ''}" @click=${(e) => { e.preventDefault(); router.navigate('/dashboard'); }}>
        <i class="fas fa-th-large" style="font-size: 20px;"></i>
        <span>${t('nav.dashboard')}</span>
      </a>
      <a href="/clients" class="bottom-nav-item ${router.currentPath === '/clients' || router.currentPath.startsWith('/clients/') ? 'active' : ''}" @click=${(e) => { e.preventDefault(); router.navigate('/clients'); }}>
        <i class="fas fa-users" style="font-size: 20px;"></i>
        <span>${t('nav.clients')}</span>
      </a>
      <a href="/settings" class="bottom-nav-item ${router.currentPath === '/settings' ? 'active' : ''}" @click=${(e) => { e.preventDefault(); router.navigate('/settings'); }}>
        <i class="fas fa-cog" style="font-size: 20px;"></i>
        <span>${t('header.configShort')}</span>
      </a>
    </nav>
    ` : ''}

    <style>
      .dashboard {
        min-height: 100vh;
        background: var(--bg-secondary);
      }

      .dashboard-header {
        background: var(--glass-bg);
        border-bottom: 1px solid var(--glass-border);
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        backdrop-filter: var(--glass-blur);
        -webkit-backdrop-filter: var(--glass-blur);
        box-shadow: var(--shadow-glass), var(--shadow-glass-inset);
      }

      .dashboard-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 24px;
        height: 72px;
        max-width: 1400px;
        margin: 0 auto;
        width: 100%;
      }

      .logo img.logo-main {
        height: 24px;
        width: auto;
        display: inline-block;
      }
      
      @media (min-width: 768px) {
        .logo img {
          height: 28px;
        }
      }


      .dashboard-menu {
        display: flex;
      }

      @media (min-width: 768px) {
        .dashboard-menu {
          gap: 8px;
        }
      }

      .nav-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        border-radius: 12px;
        text-decoration: none;
        color: var(--text-tertiary);
        font-size: 14px;
        font-weight: 600;
        position: relative;
        overflow: hidden;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        transform-style: preserve-3d;
      }

      .nav-item:hover {
        background: color-mix(in srgb, var(--primary-color, #1877f2) 8%, transparent);
        color: var(--text-primary);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      [data-theme="dark"] .nav-item:hover {
        background: color-mix(in srgb, var(--primary-color, #1877f2) 15%, transparent);
      }

      .nav-item:active:not(.active) {
        background: color-mix(in srgb, var(--primary-color, #1877f2) 25%, transparent) !important;
        color: var(--primary-color, #1877f2) !important;
        transform: scale(0.98);
      }

      [data-theme="dark"] .nav-item:active:not(.active) {
        background: color-mix(in srgb, var(--primary-color, #1877f2) 35%, transparent) !important;
      }

      .nav-item.active {
        background: linear-gradient(135deg, var(--primary-color, #1877f2) 0%, var(--secondary-color, #42B883) 100%);
        color: white;
        box-shadow:
          0 4px 12px color-mix(in srgb, var(--primary-color, #1877f2) 30%, transparent),
          0 0 30px color-mix(in srgb, var(--primary-color, #1877f2) 40%, transparent);
      }

      .nav-item svg {
        width: 20px;
        height: 20px;
      }
      
      .plan-indicator {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      
      .plan-badge {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .plan-badge.free {
        background: rgba(156, 163, 175, 0.15);
        color: var(--text-tertiary);
        border: 1px solid rgba(156, 163, 175, 0.25);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      [data-theme="dark"] .plan-badge.free {
        background: rgba(156, 163, 175, 0.2);
        border-color: rgba(156, 163, 175, 0.3);
      }
      
      .plan-badge.pro {
        background: var(--summdash-gradient);
        color: white;
        box-shadow: 0 4px 12px rgba(128, 128, 128, 0.3), 0 0 20px rgba(128, 128, 128, 0.3);
        position: relative;
        overflow: hidden;
      }
      
      .plan-badge.pro::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        transition: left 0.6s ease;
      }
      
      .plan-badge.pro:hover::before {
        left: 100%;
      }
      
      .upgrade-btn {
        padding: 6px 12px !important;
        font-size: 10px !important;
        min-height: 28px !important;
      }
      
      /* Client Counter com animação de slide */
      .client-counter-wrapper {
        position: relative;
        overflow: hidden;
        min-width: 70px;
        height: 28px;
        background: rgba(75, 85, 99, 0.15);
        border: 1px solid rgba(75, 85, 99, 0.2);
        border-radius: 16px;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .client-counter-wrapper:hover {
        background: rgba(75, 85, 99, 0.25);
        transform: scale(1.02);
      }

      [data-theme="dark"] .client-counter-wrapper {
        background: rgba(156, 163, 175, 0.2);
        border-color: rgba(156, 163, 175, 0.25);
      }

      [data-theme="dark"] .client-counter-wrapper:hover {
        background: rgba(156, 163, 175, 0.3);
      }

      .client-counter-wrapper.warning {
        background: rgba(75, 85, 99, 0.15);
        border-color: rgba(75, 85, 99, 0.2);
      }

      [data-theme="dark"] .client-counter-wrapper.warning {
        background: rgba(156, 163, 175, 0.2);
        border-color: rgba(156, 163, 175, 0.25);
      }

      .client-counter-slider {
        display: flex;
        flex-direction: column;
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .client-counter-slider.animated {
        animation: slideUpDown 15s ease-in-out infinite;
      }

      @keyframes slideUpDown {
        0%, 85% {
          transform: translateY(0);
        }
        90%, 95% {
          transform: translateY(-100%);
        }
        100% {
          transform: translateY(0);
        }
      }

      .counter-slide {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 6px 10px;
        height: 28px;
        font-size: 12px;
        font-weight: 500;
        color: #4b5563;
        white-space: nowrap;
      }

      [data-theme="dark"] .counter-slide {
        color: #9ca3af;
      }

      .counter-slide svg {
        width: 14px;
        height: 14px;
        opacity: 0.7;
        flex-shrink: 0;
      }

      .counter-upgrade {
        color: var(--primary);
        font-weight: 600;
      }

      .counter-upgrade svg {
        opacity: 1;
        stroke: var(--primary);
      }

      .client-counter-wrapper.warning .counter-info {
        color: #4b5563;
      }

      [data-theme="dark"] .client-counter-wrapper.warning .counter-info {
        color: #9ca3af;
      }

      .client-counter-wrapper.warning .counter-info svg {
        stroke: currentColor;
        opacity: 0.7;
      }

      /* Legacy styles for compatibility */
      .client-counter {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: rgba(75, 85, 99, 0.15);
        border: 1px solid rgba(75, 85, 99, 0.2);
        border-radius: 16px;
        font-size: 12px;
        font-weight: 500;
        color: #4b5563;
        transition: all 0.2s ease;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      [data-theme="dark"] .client-counter {
        background: rgba(156, 163, 175, 0.2);
        border-color: rgba(156, 163, 175, 0.25);
        color: #9ca3af;
      }

      .client-counter.warning {
        background: rgba(75, 85, 99, 0.15);
        border-color: rgba(75, 85, 99, 0.2);
        color: #4b5563;
      }

      [data-theme="dark"] .client-counter.warning {
        background: rgba(156, 163, 175, 0.2);
        border-color: rgba(156, 163, 175, 0.25);
        color: #9ca3af;
      }

      .client-counter svg {
        width: 14px;
        height: 14px;
        opacity: 0.7;
      }

      .user-menu {
        position: relative;
      }

      .user-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-left: auto;
      }

      .user-avatar-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 20px;
        transition: all 0.3s ease;
      }

      .user-avatar-wrapper:hover {
        background: rgba(var(--brand-primary-rgb, 99, 102, 241), 0.08);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      [data-theme="dark"] .user-avatar-wrapper:hover {
        background: rgba(var(--brand-primary-rgb, 99, 102, 241), 0.15);
      }

      .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid var(--border-primary);
      }

      .user-avatar-initial {
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--summdash-gradient);
        color: white;
        font-weight: 600;
        font-size: 16px;
        line-height: 1;
        text-transform: uppercase;
        box-sizing: border-box;
      }

      .dropdown-arrow {
        width: 12px;
        height: 12px;
        transition: transform 0.2s ease;
      }

      .dropdown-arrow.open {
        transform: rotate(180deg);
      }

      /* z-index 10001 para ficar acima de loading screens (9999) */
      .user-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 220px;
        background: rgba(255, 255, 255, 0.95);
        border: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
        border-radius: 16px;
        box-shadow:
          0 12px 48px rgba(0, 0, 0, 0.15),
          0 1px 0 0 rgba(255, 255, 255, 0.5) inset;
        padding: 8px;
        z-index: 10001;
        animation: dropdownSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(20px) saturate(1.8);
        -webkit-backdrop-filter: blur(20px) saturate(1.8);
      }

      [data-theme="dark"] .user-dropdown {
        background: rgba(20, 20, 20, 0.85);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow:
          0 12px 48px rgba(0, 0, 0, 0.4),
          0 1px 0 0 rgba(255, 255, 255, 0.08) inset;
      }
      

      .dropdown-header {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .user-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
      }

      .user-email {
        font-size: 12px;
        color: var(--text-secondary);
      }

      .dropdown-divider {
        height: 1px;
        background: var(--border-color, rgba(0, 0, 0, 0.1));
        margin: 8px 0;
      }

      .dropdown-item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border: none;
        background: transparent;
        color: var(--text-primary);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        border-radius: 8px;
        transition: all 0.2s ease;
        text-align: left;
      }

      .dropdown-item:hover {
        background: var(--bg-tertiary);
        color: var(--text-primary);
      }

      .dropdown-item svg {
        width: 20px;
        height: 20px;
        color: var(--text-secondary);
        stroke: currentColor;
      }

      /* Toggle de tema sempre visível no dropdown */
      .dropdown-theme-toggle {
        display: flex;
      }

      .dropdown-theme-toggle:hover {
        background: var(--bg-tertiary);
        color: var(--text-primary);
      }

      .dashboard-content {
        padding: 0;
      }

      /* Mobile Navigation */
      .mobile-header {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      
      @media (min-width: 768px) {
        .mobile-header {
          display: contents;
        }
      }
      
      .mobile-menu-button {
        display: flex !important;
        width: 40px !important;
        height: 40px !important;
        padding: 0 !important;
        min-height: 40px !important;
        border-radius: 50% !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        position: relative !important;
        overflow: hidden !important;
      }
      
      .mobile-menu-button::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--bg-tertiary);
        border-radius: 50%;
        transform: scale(0);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: -1;
      }
      
      .mobile-menu-button:hover::before {
        transform: scale(1);
      }
      
      .mobile-menu-button:hover {
        transform: scale(1.1) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      }
      
      .mobile-menu-button svg {
        width: 24px;
        height: 24px;
        stroke-width: 2;
      }
      
      /* Menu icon animation */
      .menu-icon .line {
        transition: all 0.3s ease;
        transform-origin: center;
      }
      
      .menu-icon.open .line-top {
        transform: rotate(45deg) translate(0, 6px);
      }
      
      .menu-icon.open .line-middle {
        opacity: 0;
        transform: scaleX(0);
      }
      
      .menu-icon.open .line-bottom {
        transform: rotate(-45deg) translate(0, -6px);
      }
      
      @media (min-width: 768px) {
        .mobile-menu-button {
          display: none !important;
        }
      }

      /* Mobile menu styles removed - now using external CSS from dashboard-header.css */
      
      @media (max-width: 768px) {
        .dashboard-header {
          background: rgba(255, 255, 255, 0.85) !important;
        }

        [data-theme="dark"] .dashboard-header {
          background: rgba(30, 30, 30, 0.85) !important;
        }

        .dashboard-nav {
          padding: 0 16px;
          height: 60px;
        }

        .header-right {
          gap: 8px;
          margin-left: auto;
        }

        .plan-indicator {
          display: flex;
          flex-direction: row;
          gap: 6px;
        }

        .plan-badge {
          padding: 0;
          background: transparent !important;
          border: none !important;
        }

        .plan-badge .plan-name {
          display: none;
        }

        .upgrade-btn {
          display: flex !important;
          padding: 6px 10px !important;
          font-size: 10px !important;
          min-height: 26px !important;
          gap: 4px !important;
        }

        .upgrade-btn svg {
          width: 12px !important;
          height: 12px !important;
        }

        .client-counter {
          padding: 4px 8px;
          font-size: 10px;
          gap: 4px;
        }

        .client-counter svg {
          width: 12px;
          height: 12px;
        }

        /* Mobile styles for animated counter */
        .client-counter-wrapper {
          min-width: 60px;
          height: 24px;
        }

        .counter-slide {
          padding: 4px 8px;
          height: 24px;
          font-size: 10px;
          gap: 4px;
        }

        .counter-slide svg {
          width: 12px;
          height: 12px;
        }

        /* @keyframes slideUpDown → defined above */

        .user-actions {
          gap: 8px;
        }


        .user-avatar {
          width: 32px;
          height: 32px;
        }

        .user-avatar-initial {
          font-size: 14px;
        }

        .user-dropdown {
          right: 0;
        }
      }
    </style>
  `;
};